import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Store, ListChecks, ChevronRight } from 'lucide-react';
import { useStore, nextActions, vendorStats, guardStats, fmtIDR, fmtDate, daysUntil } from '../lib/store';
import { Reveal, StatusChip, Money } from '../components/Bits';

export default function Home() {
  const { state } = useStore();
  const actions = nextActions(state);
  const hero = actions[0];
  const rest = actions.slice(1);
  const postWedding = daysUntil(state.profile.weddingDate) < 0;
  const heroPayment = state.payments.filter((p) => p.status === 'upcoming').sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1))[0];
  const heroGuard = heroPayment ? guardStats(state, heroPayment.id) : null;

  const upcoming = state.payments
    .filter((p) => p.status === 'upcoming' && daysUntil(p.dueDate) <= 14)
    .sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1))
    .slice(0, 2);

  const vStats = state.vendors.map((v) => ({ v, s: vendorStats(state, v.id) }));
  const checkedCount = vStats.filter((x) => x.s.remaining === 0).length;
  const needVendors = vStats.filter((x) => x.s.remaining > 0).sort((a, b) => b.s.remaining - a.s.remaining).slice(0, 2);

  const openTasks = state.tasks.filter((t) => !t.done).sort((a, b) => (a.targetDate < b.targetDate ? -1 : 1));
  const prepPreview = openTasks.slice(0, 2);

  const paid = state.payments.filter((p) => p.status === 'paid');
  const upcomingAll = state.payments.filter((p) => p.status === 'upcoming');
  const unreviewed = upcomingAll.filter((p) => guardStats(state, p.id).remaining > 0).length;
  const dueSoonTasks = openTasks.filter((t) => daysUntil(t.targetDate) <= 14).length;
  const reviewedTotal =
    paid.reduce((a, p) => a + p.amount, 0) +
    upcomingAll.filter((p) => guardStats(state, p.id).remaining === 0).reduce((a, p) => a + p.amount, 0);
  const needsReviewTotal = upcomingAll
    .filter((p) => guardStats(state, p.id).remaining > 0)
    .reduce((a, p) => a + p.amount, 0);
  const daysToWed = daysUntil(state.profile.weddingDate);
  const prepDone = state.tasks.filter((t) => t.done).length;
  const dueMonth = upcomingAll.filter((p) => daysUntil(p.dueDate) <= 30).length;

  return (
    <div data-testid="home-page" className="space-y-8">
      {/* WEDDING CONTEXT */}
      {!postWedding && isFinite(daysToWed) && (
        <Reveal>
          <div>
            <p data-testid="home-countdown" className="font-serif text-[26px] font-medium text-ink leading-tight tnum">{daysToWed} hari menuju hari H</p>
            <p className="mt-1 font-serif italic text-sm text-cashmere">Langkah kecil menuju hari besar kalian.</p>
          </div>
        </Reveal>
      )}

      {/* HERO ACTION */}
      {postWedding ? (
        <Reveal>
          <section data-testid="home-postwedding-hero" className="relative overflow-hidden rounded-[20px] border border-line bg-white soft-shadow p-6">
            <div className="kawung absolute inset-0 opacity-[0.03] pointer-events-none" />
            <div className="relative">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cashmere mb-4">SebelumSah</p>
              <h1 className="font-serif text-2xl font-medium tracking-tight leading-snug text-ink">Hari H sudah terlewati. 🤍</h1>
              <p className="mt-2 text-sm text-cashmere leading-relaxed">Simpan ringkasan hal penting yang sudah kalian lalui bersama.</p>
              <Link data-testid="home-postwedding-cta" to="/app/ringkasan" className="mt-5 inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-xs font-bold text-ivory hover:bg-inkdeep transition-colors">
                Lihat Ringkasan Pernikahan <ArrowRight size={14} />
              </Link>
            </div>
          </section>
        </Reveal>
      ) : (
      <Reveal>
        <section
          className="relative overflow-hidden rounded-[20px] text-ivory p-6 border border-white/[0.07] shadow-[0_12px_32px_-8px_rgba(27,36,56,0.18)]"
          style={{
            background:
              'radial-gradient(ellipse 60% 50% at 92% 8%, rgba(184,83,56,0.05) 0%, transparent 65%), linear-gradient(145deg, #131822 0%, #1A2130 55%, #0E1219 100%)',
          }}
        >
          <div className="kawung-light absolute inset-0 opacity-[0.03] pointer-events-none" />
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Yang Perlu Dibereskan</p>
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-terra opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-terra" />
              </span>
            </div>
            {hero ? (
              <>
                <h1 data-testid="home-hero-title" className="font-serif text-2xl font-medium tracking-tight leading-snug">{hero.title}</h1>
                {heroPayment && (
                  <div className="mt-3">
                    <p className="font-mono tnum text-lg font-semibold text-ivory tracking-tight">
                      {heroPayment.label} · {fmtIDR(heroPayment.amount)}
                    </p>
                    <p className="font-mono tnum text-[11px] text-ivory/60 mt-0.5">Jatuh tempo {fmtDate(heroPayment.dueDate)}</p>
                  </div>
                )}
                {heroGuard && heroGuard.remaining > 0 && (
                  <span className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-[#FBF1DF]/15 border border-[#EEDCB9]/40 text-[#F0D9B5] text-[11px] font-semibold px-3 py-1 tnum">
                    {heroGuard.remaining} hal perlu dicek
                  </span>
                )}
                <p className="mt-2.5 text-sm text-ivory/70 leading-relaxed">{hero.desc}</p>
                <Link data-testid="home-hero-cta" to={hero.to} className="mt-5 inline-flex items-center gap-2 rounded-full bg-terra px-5 py-2.5 text-xs font-bold text-ivory hover:brightness-110 transition">
                  {hero.cta} <ArrowRight size={14} />
                </Link>
              </>
            ) : (
              <>
                <h1 data-testid="home-hero-clear" className="font-serif text-2xl font-medium tracking-tight leading-snug">Semua catatan sudah klir.</h1>
                <p className="mt-2 text-sm text-ivory/70 leading-relaxed">Satu langkah besar lebih tenang menuju hari H.</p>
              </>
            )}
          </div>
        </section>
      </Reveal>
      )}

      {/* IMPACT */}
      <Reveal delay={0.03}>
        <section data-testid="home-impact" className="rounded-[20px] border border-line bg-white soft-shadow px-5 py-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-terra mb-2">Sejauh ini</p>
          <p className="text-sm text-pewter">
            <Money className="font-semibold text-sage">{fmtIDR(reviewedTotal)}</Money> telah ditinjau sebelum dibayar
          </p>
          {needsReviewTotal > 0 && (
            <p className="text-sm text-pewter mt-1">
              <Money className="font-semibold text-amberw">{fmtIDR(needsReviewTotal)}</Money> masih perlu ditinjau
            </p>
          )}
        </section>
      </Reveal>

      {rest.length > 0 && (
        <Reveal delay={0.05}>
          <section>
            <div className="space-y-2">
              {rest.map((a) => (
                <Link data-testid={`home-action-${a.key}`} key={a.key} to={a.to} className="card-lift flex items-center justify-between gap-3 rounded-xl border border-line bg-white soft-shadow px-4 py-3.5">
                  <div>
                    <p className="text-sm font-semibold text-ink leading-snug">{a.title}</p>
                    <p className="text-[11px] text-cashmere mt-0.5">{a.desc}</p>
                  </div>
                  <ArrowRight size={15} className="text-terra shrink-0" />
                </Link>
              ))}
            </div>
            <Link data-testid="home-see-all-actions" to="/app/persiapan" className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-terra hover:underline">
              Lihat semua <ArrowRight size={13} />
            </Link>
          </section>
        </Reveal>
      )}

      {/* PAYMENT GUARD RADAR */}
      <Reveal delay={0.08}>
        <section>
          <div className="flex items-baseline justify-between mb-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-terra">Pembayaran Terdekat</p>
            <Link data-testid="home-see-payments" to="/app/transaksi" className="text-[11px] font-semibold text-ink/60 hover:text-ink">Lihat semua</Link>
          </div>
          {upcoming.length === 0 ? (
            <p className="text-sm text-cashmere rounded-xl border border-line bg-white soft-shadow px-4 py-4">Semua pembayaran terkendali saat ini.</p>
          ) : (
            <div className="space-y-3">
              {upcoming.map((p) => {
                const v = state.vendors.find((x) => x.id === p.vendorId);
                const g = guardStats(state, p.id);
                return (
                  <div data-testid={`home-payment-${p.id}`} key={p.id} className="card-lift rounded-2xl border border-ink/10 bg-white p-5">
                    <p className="text-[11px] font-semibold text-slate-500">{v?.name}</p>
                    <div className="mt-1.5 flex items-baseline justify-between gap-2">
                      <p className="text-sm font-bold text-ink">{p.label}</p>
                      <Money className="text-lg font-semibold text-ink">{fmtIDR(p.amount)}</Money>
                    </div>
                    <p className="font-mono text-[11px] text-slate-500 mt-1">{fmtDate(p.dueDate)}</p>
                    <div className="mt-3 flex items-center justify-between gap-3">
                      {g.remaining > 0 ? (
                        <StatusChip done={false} todoLabel={`${g.remaining} hal perlu dicek sebelum bayar`} />
                      ) : (
                        <StatusChip done doneLabel="Checklist utama sudah ditinjau" />
                      )}
                      <Link data-testid={`home-guard-cta-${p.id}`} to={`/app/pembayaran/${p.id}/guard`} className="inline-flex items-center gap-1.5 text-xs font-bold text-terra hover:underline shrink-0">
                        <ShieldCheck size={13} /> Buka Payment Guard
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </Reveal>

      {/* COMPACT STATUS */}
      <Reveal delay={0.1}>
        <section data-testid="home-status" className="rounded-[20px] border border-line bg-white soft-shadow divide-y divide-line overflow-hidden">
          <Link data-testid="status-prep" to="/app/persiapan" className="flex items-center gap-3.5 px-5 py-4 hover:bg-bone/60 transition-colors">
            <ListChecks size={17} className="text-terra shrink-0" strokeWidth={1.8} />
            <p className="flex-1 text-sm font-semibold text-ink">Persiapan</p>
            <p className="font-mono tnum text-[11px] text-cashmere">{prepDone} dari {state.tasks.length} sudah beres</p>
            <ChevronRight size={14} className="text-cashmere/60" />
          </Link>
          <Link data-testid="status-vendor" to="/app/transaksi" className="flex items-center gap-3.5 px-5 py-4 hover:bg-bone/60 transition-colors">
            <Store size={17} className="text-terra shrink-0" strokeWidth={1.8} />
            <p className="flex-1 text-sm font-semibold text-ink">Vendor</p>
            <p className="font-mono tnum text-[11px] text-cashmere">{checkedCount} sudah dicek · {vStats.length - checkedCount} perlu dicek</p>
            <ChevronRight size={14} className="text-cashmere/60" />
          </Link>
          <Link data-testid="status-payment" to="/app/transaksi" className="flex items-center gap-3.5 px-5 py-4 hover:bg-bone/60 transition-colors">
            <ShieldCheck size={17} className="text-terra shrink-0" strokeWidth={1.8} />
            <p className="flex-1 text-sm font-semibold text-ink">Pembayaran</p>
            <p className="font-mono tnum text-[11px] text-cashmere">{dueMonth} jatuh tempo bulan ini</p>
            <ChevronRight size={14} className="text-cashmere/60" />
          </Link>
        </section>
      </Reveal>
    </div>
  );
}
